import React, { useState, useEffect } from 'react';
import { PitchDeck, PitchDeckSlide } from '../../types/pitchDeck';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
// Import components from the barrel file
import { SlideEditor, SlidePreview, SlideList } from '.';
import { supabase } from '../../lib/supabase';

interface PitchDeckEditorProps {
  onBackClick: () => void;
  deckId: string;
  initialDeckData: PitchDeck;
  selectedSlideId: string | null;
  onSlideSelect: (slideId: string | null) => void;
}

export const PitchDeckEditor: React.FC<PitchDeckEditorProps> = ({ 
  onBackClick, 
  deckId, 
  initialDeckData, 
  selectedSlideId,
  onSlideSelect
 }) => {
  // Local state for the editor
  const [currentDeck, setCurrentDeck] = useState<PitchDeck>(initialDeckData);
  const [showPreviewMode, setShowPreviewMode] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Effect to update deck if initial data changes
  useEffect(() => {
    setCurrentDeck(initialDeckData);
  }, [initialDeckData]);
  const [exportType, setExportType] = useState<'pdf' | 'pptx' | 'images' | null>(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showAddSlideMenu, setShowAddSlideMenu] = useState(false);
  
  // Handle title change
  const handleTitleChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const newTitle = e.target.value;
    setCurrentDeck(prev => ({ ...prev, title: newTitle }));
    
    try {
      const { error } = await supabase
        .from('pitch_decks')
        .update({ title: newTitle })
        .eq('id', deckId);
        
      if (error) throw error;
    } catch (err: any) {
      console.error('Error updating deck title:', err);
      toast.error('Failed to update title');
      setError('Failed to update title');
    }
  };
  
  // Slide management functions - all slide data computation moved to the render section

  // Function to update a slide's content
  const handleUpdateSlide = async (updatedSlide: PitchDeckSlide) => {
    try {
      setIsLoading(true);
      setError(null);

      // Update the slide in the current deck
      const updatedDeck = {
        ...currentDeck,
        slides: currentDeck.slides.map(slide => 
          slide.id === updatedSlide.id ? updatedSlide : slide
        )
      };
      
      // Update in Supabase
      const { error: supabaseError } = await supabase
        .from('pitch_deck_slides')
        .update({
          title: updatedSlide.title,
          content: updatedSlide.content,
          style: updatedSlide.style,
          updated_at: new Date().toISOString()
        })
        .eq('id', updatedSlide.id);
      
      if (supabaseError) throw supabaseError;
      
      // Update local state
      setCurrentDeck(updatedDeck);
      toast.success('Slide updated');
    } catch (err: any) {
      console.error('Error updating slide:', err);
      toast.error('Failed to update slide');
      setError('Failed to update slide');
    } finally {
      setIsLoading(false);
    }
  };

  // Function to generate slide content using AI
  const handleGenerateSlideContent = async (slideId: string) => {
    try {
      setIsGenerating(true);
      setError(null);
      
      // Find the slide to generate content for
      const slideToUpdate = currentDeck.slides.find(s => s.id === slideId);
      if (!slideToUpdate) {
        toast.error('Slide not found');
        return;
      }
      
      // Simulating AI generation with a timeout
      toast.info('Generating slide content...');
      
      // In a real implementation, this would call an AI service
      // For now we'll just add some placeholder content after a delay
      setTimeout(() => {
        // Generate content as a string as per the PitchDeckSlide interface
        const generatedContent = `# ${slideToUpdate.title}

This would contain AI-generated content based on the slide title and type.

Generated for slide type: ${slideToUpdate.type}`;
        
            
        const updatedSlide = {
          ...slideToUpdate,
          content: generatedContent 
        };
        
        // Update the slide with the generated content
        handleUpdateSlide(updatedSlide);
        setIsGenerating(false);
        toast.success('Content generated successfully');
      }, 2000); // Simulate API delay
      
    } catch (err: any) {
      console.error('Error generating slide content:', err);
      toast.error('Failed to generate content');
      setError('Failed to generate content');
      setIsGenerating(false);
    }
  };

  // Slide management functions
  const handleAddSlide = async (type: PitchDeckSlide['type']) => {
    setIsGenerating(true);
    setError(null);
    
    try {
      // Create a new slide with default content and generated ID
      const slideId = crypto.randomUUID();
      const now = new Date().toISOString();
      const newSlide: PitchDeckSlide = {
        id: slideId,
        deckId: deckId,
        title: `New ${type} slide`,
        content: '',
        type,
        order: currentDeck.slides.length,
        style: { backgroundColor: '#ffffff', textColor: '#000000' },
        createdAt: now,
        updatedAt: now
      };
      
      // First update local state for immediate feedback
      const updatedSlides = [...currentDeck.slides, newSlide];
      setCurrentDeck(prev => ({ ...prev, slides: updatedSlides }));
      
      // Then update in Supabase
      const { error } = await supabase
        .from('pitch_decks')
        .update({ slides: updatedSlides })
        .eq('id', deckId);
        
      if (error) throw error;
      
      // Select the new slide
      onSlideSelect(slideId);
      
      toast.success('Slide added successfully');
    } catch (err: any) {
      console.error('Error adding slide:', err);
      toast.error('Failed to add slide');
      setError('Failed to add slide');
    } finally {
      setIsGenerating(false);
    }
  };
  
  // Function to reorder slides
  const reorderSlides = async (reorderedSlides: PitchDeckSlide[]) => {
    try {
      // Update order property based on new positions
      const updatedSlides = reorderedSlides.map((slide, index) => ({
        ...slide,
        order: index
      }));
      
      // Update local state
      setCurrentDeck(prev => ({ ...prev, slides: updatedSlides }));
      
      // Update in Supabase
      const { error } = await supabase
        .from('pitch_decks')
        .update({ slides: updatedSlides })
        .eq('id', deckId);
        
      if (error) throw error;
    } catch (err: any) {
      console.error('Error reordering slides:', err);
      toast.error('Failed to reorder slides');
      setError('Failed to reorder slides');
    }
  };
  
  // Function to remove a slide
  const removeSlide = async (slideId: string) => {
    try {
      // Filter out the slide to remove
      const updatedSlides = currentDeck.slides
        .filter(slide => slide.id !== slideId)
        .map((slide, index) => ({ ...slide, order: index })); // Update order
      
      // Update local state
      setCurrentDeck(prev => ({ ...prev, slides: updatedSlides }));
      
      // If removed slide was selected, clear selection
      if (selectedSlideId === slideId) {
        onSlideSelect(null);
      }
      
      // Update in Supabase
      const { error } = await supabase
        .from('pitch_decks')
        .update({ slides: updatedSlides })
        .eq('id', deckId);
        
      if (error) throw error;
      
      toast.success('Slide removed');
    } catch (err: any) {
      console.error('Error removing slide:', err);
      toast.error('Failed to remove slide');
      setError('Failed to remove slide');
    }
  };
  
  // Helper function for slide selection
  const selectSlide = (slideId: string | null) => {
    onSlideSelect(slideId);
  };

  // Handler for slide selection from UI components
  const handleSlideSelect = (slideId: string) => {
    onSlideSelect(slideId);
  };

  const handleSlideRemove = async (slideId: string) => {
    if (window.confirm('Are you sure you want to delete this slide?')) {
      setIsLoading(true);
      setError(null);
      
      try {
        // Update local state first
        const updatedSlides = currentDeck.slides.filter(slide => slide.id !== slideId);
        setCurrentDeck(prev => ({ ...prev, slides: updatedSlides }));
        
        // Then update in Supabase
        const { error } = await supabase
          .from('pitch_decks')
          .update({ slides: updatedSlides })
          .eq('id', deckId);
          
        if (error) throw error;
        
        // If the deleted slide was selected, clear selection
        if (selectedSlideId === slideId) {
          onSlideSelect(null);
        }
        
        toast.success('Slide removed successfully');
      } catch (err: any) {
        console.error('Error removing slide:', err);
        toast.error('Failed to remove slide');
        setError('Failed to remove slide');
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleSlideReorder = async (newOrder: string[]) => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Create a new slides array with updated order
      const reorderedSlides = [...currentDeck.slides];
      newOrder.forEach((slideId, index) => {
        const slide = reorderedSlides.find(s => s.id === slideId);
        if (slide) {
          slide.order = index;
        }
      });
      
      // Sort by the new order
      reorderedSlides.sort((a, b) => a.order - b.order);
      
      // Update local state
      setCurrentDeck(prev => ({ ...prev, slides: reorderedSlides }));
      
      // Update in Supabase
      const { error } = await supabase
        .from('pitch_decks')
        .update({ slides: reorderedSlides })
        .eq('id', deckId);
        
      if (error) throw error;
      
      toast.success('Slides reordered successfully');
    } catch (err: any) {
      console.error('Error reordering slides:', err);
      toast.error('Failed to reorder slides');
      setError('Failed to reorder slides');
    } finally {
      setIsLoading(false);
    }
  };

  // Stub functions for export capabilities
  const exportToPdf = (deckId: string, slides: PitchDeckSlide[]) => {
    // Stub function for PDF export
    console.log('PDF export not yet implemented');
    toast.info('PDF export functionality coming soon!');
    return;
  };

  const exportToPowerPoint = (deckId: string, slides: PitchDeckSlide[]) => {
    // Stub function for PowerPoint export
    console.log('PowerPoint export not yet implemented');
    toast.info('PowerPoint export functionality coming soon!');
    return;
  };

  const exportToImageSequence = (deckId: string, slides: PitchDeckSlide[]) => {
    // Stub function for Image sequence export
    console.log('Image export not yet implemented');
    toast.info('Image export functionality coming soon!');
    return;
  };

  // Handle export operations
  const handleExport = async (type: 'pdf' | 'pptx' | 'images') => {
    setExportType(type);
    setIsExporting(true);
    setShowExportMenu(false);
    
    try {
      switch (type) {
        case 'pdf':
          exportToPdf(deckId, currentDeck.slides);
          break;
          
        case 'pptx':
          exportToPowerPoint(deckId, currentDeck.slides);
          break;
          
        case 'images':
          exportToImageSequence(deckId, currentDeck.slides);
          break;
      }
    } catch (err: any) {
      console.error(`Error exporting to ${type}:`, err);
      toast.error(`Failed to export as ${type}: ${err}`);
      setError(`Export failed: ${err}`);
    } finally {
      setIsExporting(false);
      setExportType(null);
      setShowExportMenu(false);
    }
  };

  // If no deck is loaded, show a loading indicator
  if (!currentDeck) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700 mx-auto"></div>
          <p className="mt-3 text-gray-600">Loading pitch deck...</p>
        </div>
      </div>
    );
  }

  // Get the currently selected slide if any
  const selectedSlide = selectedSlideId 
    ? currentDeck.slides.find(slide => slide.id === selectedSlideId) 
    : null;
  
  // Sort slides by order
  const sortedSlides = [...currentDeck.slides].sort((a, b) => a.order - b.order);
  
  return (
    <div className="w-full bg-white rounded-lg shadow-md overflow-hidden">
      {/* Editor Header */}
      <header className="bg-white border-b border-gray-200 p-4 flex justify-between items-center sticky top-0 z-10">
        <div className="flex items-center">
          <button 
            onClick={onBackClick}
            className="mr-4 text-gray-600 hover:text-gray-900 transition-colors"
          >
            &larr; Back
          </button>
          <input
            type="text"
            value={currentDeck?.title || ''}
            onChange={handleTitleChange}
            className="text-xl font-semibold border-none focus:ring-0 focus:outline-none"
            placeholder="Untitled Pitch Deck"
          />
        </div>
          
        <div className="flex items-center space-x-2">
          <button
            onClick={() => setShowPreviewMode(!showPreviewMode)}
            className={`px-3 py-1 rounded ${
              showPreviewMode 
                ? 'bg-blue-600 text-white' 
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {showPreviewMode ? 'Edit Mode' : 'Preview Mode'}
          </button>
          
          <div className="relative">
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              className="px-3 py-1 rounded bg-green-600 text-white hover:bg-green-700"
              disabled={isExporting}
            >
              {isExporting ? `Exporting ${exportType}...` : 'Export'}
            </button>
            
            {showExportMenu && (
              <div className="absolute right-0 mt-1 w-48 bg-white rounded-md shadow-lg z-10">
                <ul className="py-1">
                  <li>
                    <button
                      onClick={() => handleExport('pdf')}
                      className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100"
                    >
                      Export as PDF
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => handleExport('pptx')}
                      className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100"
                    >
                      Export as PowerPoint
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => handleExport('images')}
                      className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100"
                    >
                      Export as Images
                    </button>
                  </li>
                </ul>
              </div>
            )}
          </div>
        </div>
      </header>
      
      {/* Main Editor Area */}
      <div className={`flex ${showPreviewMode ? 'flex-col' : 'h-[calc(100vh-12rem)]'}`}>
        {/* Sidebar with slide thumbnails */}
        {!showPreviewMode && (
          <div className="w-64 bg-gray-50 border-r border-gray-200 overflow-y-auto">
            <div className="p-3 border-b border-gray-200 flex justify-between items-center">
              <h3 className="font-medium">Slides</h3>
              <div className="relative">
                <button 
                  className="text-blue-600 hover:text-blue-800 font-medium text-lg"
                  onClick={() => setShowAddSlideMenu(prev => !prev)}
                >
                  +
                </button>
                
                {showAddSlideMenu && (
                  <div className="absolute left-0 mt-1 w-48 bg-white rounded-md shadow-lg z-10">
                    <ul className="py-1">
                      <li>
                        <button
                          onClick={() => {
                            handleAddSlide('title');
                            setShowAddSlideMenu(false);
                          }}
                          className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100"
                        >
                          Add Title Slide
                        </button>
                      </li>
                      <li>
                        <button
                          onClick={() => {
                            handleAddSlide('content');
                            setShowAddSlideMenu(false);
                          }}
                          className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100"
                        >
                          Add Content Slide
                        </button>
                      </li>
                      <li>
                        <button
                          onClick={() => {
                            handleAddSlide('image');
                            setShowAddSlideMenu(false);
                          }}
                          className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100"
                        >
                          Add Image Slide
                        </button>
                      </li>
                      <li>
                        <button
                          onClick={() => {
                            handleAddSlide('chart');
                            setShowAddSlideMenu(false);
                          }}
                          className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100"
                        >
                          Add Chart Slide
                        </button>
                      </li>
                      <li>
                        <button
                          onClick={() => {
                            handleAddSlide('quote');
                            setShowAddSlideMenu(false);
                          }}
                          className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100"
                        >
                          Add Quote Slide
                        </button>
                      </li>
                      <li>
                        <button
                          onClick={() => {
                            handleAddSlide('team');
                            setShowAddSlideMenu(false);
                          }}
                          className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-100"
                        >
                          Add Team Slide
                        </button>
                      </li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
            
            <SlideList 
              slides={sortedSlides}
              selectedSlideId={selectedSlideId}
              onSelectSlide={selectSlide}
              onReorderSlides={reorderSlides}
              onRemoveSlide={removeSlide}
            />
          </div>
        )}
        
        {/* Main content area */}
        <div className={`flex-1 ${showPreviewMode ? 'w-full' : 'overflow-y-auto'}`}>
          {showPreviewMode ? (
            <div className="py-8">
              {sortedSlides.map(slide => (
                <div key={slide.id} className="mb-6">
                  <SlidePreview slide={slide} theme={currentDeck.theme} />
                </div>
              ))}
            </div>
          ) : selectedSlide ? (
            <SlideEditor 
              slide={selectedSlide} 
              theme={currentDeck.theme} 
              isGenerating={isGenerating}
              onSlideUpdate={handleUpdateSlide}
              onSlideGenerate={handleGenerateSlideContent}
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <div className="text-center text-gray-500">
                <p className="mb-4">Select a slide to edit or preview</p>
                <button
                  onClick={() => handleAddSlide('title')}
                  className="px-4 py-2 bg-blue-600 text-white font-medium rounded-md hover:bg-blue-700"
                >
                  Add First Slide
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PitchDeckEditor;
